/**
 * PartnerKeysPage — Hospital Admin / IT Admin / Super Admin manage partner API keys here.
 *
 * Route: /integrations/partner-keys
 *
 * Capabilities:
 *  - List existing keys with status (active/revoked), env (test/live), recent usage
 *  - Issue a new key — secret shown ONCE in a copy-friendly modal
 *  - Revoke a key (with reason)
 *  - Rotate a key (revokes old, issues new with same settings)
 *  - View recent partner calls (booking + cancellation audit log)
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import {
  Key, Plus, Copy, RefreshCw, Trash2, ShieldAlert, FileCheck2, Globe, Clock,
  Activity, ChevronRight, Loader2, AlertCircle, CheckCircle2, ExternalLink,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';

const partnerKeysAPI = {
  list: () => api.get('/partner-keys/'),
  create: (data) => api.post('/partner-keys/', data),
  revoke: (id, reason) => api.post(`/partner-keys/${id}/revoke`, { reason }),
  rotate: (id) => api.post(`/partner-keys/${id}/rotate`),
  recentCalls: (id) => api.get(`/partner-keys/${id}/recent-calls`),
};

const PartnerKeysPage = () => {
  const { user, hospital, hasPermission } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [revealed, setRevealed] = useState(null); // {secret_show_once, ...metadata}
  const [callsKey, setCallsKey] = useState(null);
  const [recentCalls, setRecentCalls] = useState([]);
  const [newForm, setNewForm] = useState({ client_id: '', env: 'test', allowed_origins: '', rate_limit_per_min: 60 });
  const [saving, setSaving] = useState(false);

  const canManage = user?.is_super_admin || hasPermission?.('staff.manage') || hasPermission?.('users.manage');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Super Admin selects a hospital via the sidebar dropdown — stored in localStorage and
  // sent as X-Hospital-Id by the api client (see /app/frontend/src/lib/api.js).
  // For non-super-admin users, the hospital comes from their user record.
  const selectedHospitalId = typeof window !== 'undefined' ? localStorage.getItem('selectedHospitalId') : null;
  const needsHospitalContext = user?.is_super_admin && !selectedHospitalId && !hospital;

  const load = async () => {
    setLoading(true);
    try {
      const r = await partnerKeysAPI.list();
      setKeys(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (canManage) load(); }, [canManage]);

  const handleCreate = async () => {
    if (!newForm.client_id.trim()) { toast.error('Client label is required'); return; }
    setSaving(true);
    try {
      const r = await partnerKeysAPI.create({
        client_id: newForm.client_id.trim(),
        env: newForm.env,
        allowed_origins: newForm.allowed_origins.split(',').map(s => s.trim()).filter(Boolean),
        rate_limit_per_min: parseInt(newForm.rate_limit_per_min) || 60,
      });
      setNewOpen(false);
      setRevealed(r.data);
      setRevealOpen(true);
      setNewForm({ client_id: '', env: 'test', allowed_origins: '', rate_limit_per_min: 60 });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally { setSaving(false); }
  };

  const handleRevoke = async (k) => {
    const reason = window.prompt(`Revoke key "${k.client_id}"? (Optional reason)`);
    if (reason === null) return;
    try {
      await partnerKeysAPI.revoke(k.id, reason);
      toast.success('Key revoked');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRotate = async (k) => {
    if (!window.confirm(`Rotate key for "${k.client_id}"? The old key stops working immediately.`)) return;
    try {
      const r = await partnerKeysAPI.rotate(k.id);
      setRevealed(r.data);
      setRevealOpen(true);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openRecentCalls = async (k) => {
    setCallsKey(k);
    try {
      const r = await partnerKeysAPI.recentCalls(k.id);
      setRecentCalls(r.data || []);
    } catch { setRecentCalls([]); }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied');
  };

  if (!canManage) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-slate-600 font-semibold">Restricted</p>
        <p className="text-xs text-slate-500 mt-1">Partner API key management is available to Hospital Admin, IT Admin, and Super Admin only.</p>
      </div>
    );
  }

  if (needsHospitalContext) {
    return (
      <div className="bg-white rounded-xl border border-amber-200 p-8 text-center" data-testid="partner-keys-no-hospital">
        <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-slate-600 font-semibold">Select a hospital first</p>
        <p className="text-xs text-slate-500 mt-1">Partner API keys are tied to one hospital. Pick a hospital from the <strong>MANAGE HOSPITAL</strong> dropdown in the sidebar to continue.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="partner-keys-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white shadow-md">
            <Key size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Partner API Keys</h1>
            <p className="text-sm text-slate-500">Issue API keys for partner websites and call-centres to book appointments via <code className="text-xs bg-slate-100 px-1 rounded">/api/public/v1/*</code>.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <a href={`${backendUrl}/api/public/v1/docs`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-slate-600 px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50" data-testid="partner-docs-link">
            <ExternalLink size={12} /> Open Swagger Docs
          </a>
          <Button onClick={() => setNewOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="partner-key-new">
            <Plus className="h-4 w-4 mr-1.5" /> Issue New Key
          </Button>
        </div>
      </div>

      {/* Keys list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
          <Loader2 className="animate-spin" size={20} /> Loading keys…
        </div>
      ) : keys.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-slate-400">
            <Key className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm font-semibold text-slate-600">No partner keys yet</p>
            <p className="text-xs mt-1">Issue a test key to let your partner integrate, then switch to a live key on go-live.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left">Client / Partner</th>
                <th className="px-4 py-3 text-left">Env</th>
                <th className="px-4 py-3 text-left">Key Prefix</th>
                <th className="px-4 py-3 text-right">Rate / min</th>
                <th className="px-4 py-3 text-right">Requests</th>
                <th className="px-4 py-3 text-left">Last Used</th>
                <th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map(k => (
                <tr key={k.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`partner-key-row-${k.id}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-semibold text-slate-800">{k.client_id}</p>
                    {k.allowed_origins?.length > 0 && <p className="text-[10px] text-slate-400 mt-0.5">Origins: {k.allowed_origins.join(', ')}</p>}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={k.env === 'live' ? 'border-emerald-400 text-emerald-700' : 'border-amber-400 text-amber-700'}>{k.env}</Badge>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-slate-600">{k.key_prefix}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{k.rate_limit_per_min}</td>
                  <td className="px-4 py-2.5 text-right font-mono">{k.request_count || 0}</td>
                  <td className="px-4 py-2.5 text-slate-500">{k.last_used_at ? new Date(k.last_used_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}</td>
                  <td className="px-4 py-2.5">
                    {k.is_active ? <Badge className="bg-emerald-100 text-emerald-700">Active</Badge> : <Badge variant="outline" className="border-rose-300 text-rose-600">Revoked</Badge>}
                  </td>
                  <td className="px-4 py-2.5 text-right space-x-1">
                    {k.is_active && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => openRecentCalls(k)} data-testid={`partner-key-calls-${k.id}`} title="Recent calls"><Activity size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-indigo-600" onClick={() => handleRotate(k)} data-testid={`partner-key-rotate-${k.id}`} title="Rotate"><RefreshCw size={12} /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-rose-600" onClick={() => handleRevoke(k)} data-testid={`partner-key-revoke-${k.id}`} title="Revoke"><ShieldAlert size={12} /></Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New key dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-md" data-testid="partner-key-new-dialog">
          <DialogHeader>
            <DialogTitle>Issue a New Partner Key</DialogTitle>
            <DialogDescription>The full key will be shown <strong>only once</strong> after creation — save it securely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs">Client Label</Label>
              <Input value={newForm.client_id} onChange={e => setNewForm(f => ({ ...f, client_id: e.target.value }))} placeholder="e.g., prashanti-website" data-testid="partner-key-client-id" />
            </div>
            <div>
              <Label className="text-xs">Environment</Label>
              <Select value={newForm.env} onValueChange={v => setNewForm(f => ({ ...f, env: v }))}>
                <SelectTrigger data-testid="partner-key-env"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="test">Test (test_…) — no SMS/email/payments</SelectItem>
                  <SelectItem value="live">Live (live_…) — real bookings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Allowed Origins (CORS, comma-separated)</Label>
              <Input value={newForm.allowed_origins} onChange={e => setNewForm(f => ({ ...f, allowed_origins: e.target.value }))} placeholder="https://prashanti.com,https://partner.example.com" data-testid="partner-key-origins" />
              <p className="text-[10px] text-slate-400 mt-1">Leave blank to allow any origin (server-to-server). For browser apps, list the exact origins.</p>
            </div>
            <div>
              <Label className="text-xs">Rate Limit (requests per minute)</Label>
              <Input type="number" min="1" max="600" value={newForm.rate_limit_per_min} onChange={e => setNewForm(f => ({ ...f, rate_limit_per_min: e.target.value }))} data-testid="partner-key-rate" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="partner-key-create-submit">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Plus className="h-4 w-4 mr-1.5" />} Issue Key
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reveal dialog — secret_show_once */}
      <Dialog open={revealOpen} onOpenChange={(o) => { setRevealOpen(o); if (!o) setRevealed(null); }}>
        <DialogContent className="max-w-lg" data-testid="partner-key-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Key Created — Save it Now</DialogTitle>
            <DialogDescription>
              This is the <strong>only time</strong> the full key will be shown. Save it in your partner&rsquo;s secret manager.
            </DialogDescription>
          </DialogHeader>
          {revealed && (
            <div className="space-y-3 py-2">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 font-mono text-xs break-all" data-testid="partner-key-secret-display">
                {revealed.secret_show_once}
              </div>
              <Button onClick={() => copy(revealed.secret_show_once)} variant="outline" className="w-full" data-testid="partner-key-copy">
                <Copy className="h-4 w-4 mr-1.5" /> Copy to Clipboard
              </Button>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <p className="font-semibold mb-1">⚠ Important</p>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>This secret cannot be retrieved again — store it now.</li>
                  <li>Use header <code className="bg-amber-100 px-1 rounded">Authorization: Bearer &lt;key&gt;</code></li>
                  <li>{revealed.env === 'test' ? 'Test key: bookings carry is_test=true, no SMS/email/payments.' : 'LIVE key: real bookings.'}</li>
                </ul>
              </div>
              <Button onClick={() => setRevealOpen(false)} className="w-full">I&rsquo;ve Saved It</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recent calls drawer */}
      <Dialog open={!!callsKey} onOpenChange={(o) => !o && setCallsKey(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Recent calls — {callsKey?.client_id}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {recentCalls.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-8">No calls yet</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-slate-50">
                  <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Action</th><th className="px-3 py-2 text-left">Resource</th></tr>
                </thead>
                <tbody>
                  {recentCalls.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-1.5">{new Date(c.timestamp).toLocaleString('en-IN')}</td>
                      <td className="px-3 py-1.5"><Badge variant="outline" className={c.action.includes('BOOK') ? 'border-emerald-300 text-emerald-700' : 'border-rose-300 text-rose-600'}>{c.action}</Badge></td>
                      <td className="px-3 py-1.5 font-mono text-slate-500">{c.resource_id?.slice(0, 18)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerKeysPage;
